class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.handleViewCartClick.bind(this));
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  async renderContents(parsedState) {
    this.cartItemKey = parsedState?.key;

    try {
      let sections = parsedState?.sections;
      if (!this.hasRenderableSections(sections)) {
        sections = await this.fetchNotificationSections();
      }

      this.getSectionsToRender().forEach((section) => {
        const target = document.getElementById(section.id);
        const sectionContents = this.getSectionInnerHTML(sections[section.id], section.selector);

        if (!target || sectionContents === null) {
          throw new Error(`Cart notification section is missing: ${section.id}`);
        }

        target.innerHTML = sectionContents;
      });

      if (this.header) this.header.reveal();
      this.open();

      const cartDrawer = document.querySelector('cart-drawer');
      cartDrawer?.refreshContents?.().catch(() => {});
    } catch (error) {
      console.error('Unable to render the cart notification.', error);
      window.location.href = routes.cart_url;
    }
  }

  hasRenderableSections(sections) {
    return (
      sections &&
      typeof sections === 'object' &&
      this.getSectionsToRender().every((section) => typeof sections[section.id] === 'string')
    );
  }

  async fetchNotificationSections() {
    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    const sectionsUrl = new URL(window.location.href);
    sectionsUrl.search = '';
    sectionsUrl.searchParams.set('sections', sectionIds.join(','));

    const response = await fetch(sectionsUrl.toString());

    if (!response.ok) throw new Error('Cart notification section request failed');

    const sections = await response.json();
    if (!this.hasRenderableSections(sections)) {
      throw new Error('Cart notification sections are unavailable');
    }

    return sections;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: this.cartItemKey ? `[id="cart-notification-product-${this.cartItemKey}"]` : '.cart-item',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;

    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  async handleViewCartClick(evt) {
    const viewCartButton = evt.target.closest('#cart-notification-button');
    if (!viewCartButton) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    evt.preventDefault();

    try {
      await cartDrawer.refreshContents();
      this.close();
      cartDrawer.open(viewCartButton);
    } catch (error) {
      window.location.href = viewCartButton.href;
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
